import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Button, Icon } from '@cbrebuild/blocks';
import superFetch from '../../utils/super-fetch';
import './admin-voucher-submission-table.scss';

// Helper to safely obtain a string email from various backend shapes
const extractEmail = (emailValue) => {
  if (!emailValue) return '';
  if (typeof emailValue === 'string') return emailValue;
  if (typeof emailValue === 'object' && typeof emailValue.work === 'string' && emailValue.work.trim() !== '') {
    return emailValue.work;
  }
  return '';
};

const AdminConnectorVoucherAccessNew = ({ 
  dealId,
  connectorId,
  dealType, // Current deal's deal_type
  adminUserId,
  dealUsers, 
  dealTeams,
  dealRestrictions = null, // Passed from parent to avoid duplicate fetch
}) => {
  const [showHistory, setShowHistory] = useState(false);
  const [authorizationData, setAuthorizationData] = useState(null);
  const [historyData, setHistoryData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [adminNotes, setAdminNotes] = useState({});
  const [processingUsers, setProcessingUsers] = useState({});

  // Fetch voucher authorization data from API
  const fetchAuthorizationData = async () => {
    if (!dealId) return;
    
    try {
      setLoading(true);
      const response = await superFetch(`/admin/voucher-auth?deal_id=${dealId}`, { method: 'GET' });
      
      // Parse JSON from response
      const data = await response.json();
      
      setAuthorizationData(data);
    } catch (error) {
      console.error('Error fetching voucher authorization data');
      setAuthorizationData({ authorizations: [], count: 0 });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAuthorizationData();
  }, [dealId]);

  // Fetch voucher history data from API
  const fetchHistoryData = async () => {
    if (!dealId) return;
    
    try {
      setHistoryLoading(true);
      
      const response = await superFetch(`/admin/voucher-auth/history?deal_id=${dealId}`, { method: 'GET' });
      const data = await response.json();
      
      setHistoryData(data);
    } catch (error) {
      console.error('Error fetching voucher history data');
      setHistoryData({ history: [], count: 0 });
    } finally {
      setHistoryLoading(false);
    }
  };

  // Handle view history button click
  const handleViewHistory = () => {
    if (!showHistory) {
      // Always fetch fresh history data when opening history view
      fetchHistoryData();
    }
    setShowHistory(!showHistory);
  };

  // API function to activate/revoke user access
  const updateUserAccess = async (userId, action) => {
    if (!dealId || !userId || !adminUserId) {
      console.error('Missing required parameters for user access update');
      return false;
    }
    try {
      setProcessingUsers(prev => ({ ...prev, [userId]: true }));

      const requestBody = {
        deal_id: parseInt(dealId),
        authorized_user_id: parseInt(userId),
        admin_user_id: parseInt(adminUserId),
        connector_id: connectorId || null, // Can be null for restrictions-based flow
        action: action, // 'activate' or 'revoke'
        is_active: action === 'activate', // true for activate, false for revoke
        admin_notes: adminNotes[userId] || ''
      };


const response = await superFetch('/admin/voucher-auth', {
  method: 'POST'
}, JSON.stringify(requestBody));


      // Check if response is successful
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      // Parse the response to get the updated record
      const responseData = await response.json();

      // OPTIMIZATION: Instead of calling GET API again, update local state directly
      // This is much faster and reduces unnecessary API calls
      if (responseData && responseData.id && authorizationData) {
        
        setAuthorizationData(prevData => {
          const updatedAuthorizations = [...(prevData.authorizations || [])];
          
          // Find existing record for this user using authorized_user.id
          const existingIndex = updatedAuthorizations.findIndex(
            auth => auth.authorized_user_id === parseInt(userId) || 
                   (auth.authorized_user && auth.authorized_user.id === parseInt(userId))
          );
          
          if (existingIndex >= 0) {
            // Update existing record with the response data structure
            updatedAuthorizations[existingIndex] = {
              ...updatedAuthorizations[existingIndex],
              id: responseData.id,
              authorized_user_id: responseData.authorized_user.id,
              authorized_user: responseData.authorized_user,
              admin_user: responseData.admin_user,
              action: responseData.action,
              is_active: responseData.is_active,
              is_voucher_submitted: responseData.is_voucher_submitted,
              voucher_submitted_date: responseData.voucher_submitted_date,
              admin_notes: responseData.admin_notes,
              created: responseData.created,
              modified: responseData.modified,
              created_at: responseData.created, // Also keep created_at for compatibility
            };
          } else {
            // Add new record with the complete response structure
            const newRecord = {
              id: responseData.id,
              authorized_user_id: responseData.authorized_user.id,
              authorized_user: responseData.authorized_user,
              admin_user: responseData.admin_user,
              action: responseData.action,
              is_active: responseData.is_active,
              is_voucher_submitted: responseData.is_voucher_submitted,
              voucher_submitted_date: responseData.voucher_submitted_date,
              admin_notes: responseData.admin_notes,
              created: responseData.created,
              modified: responseData.modified,
              created_at: responseData.created, // Also keep created_at for compatibility
            };
            updatedAuthorizations.push(newRecord);
          }
          
          const updatedData = {
            ...prevData,
            authorizations: updatedAuthorizations,
            count: updatedAuthorizations.length
          };
          
          return updatedData;
        });
        
      } else {
        // Fallback: If local update fails, refresh all data
        await fetchAuthorizationData();
      }
      
      // Clear the admin notes for this user after successful submission
      setAdminNotes(prev => ({ ...prev, [userId]: '' }));

      return responseData;
    } catch (error) {
      console.error('Error updating user access');
      return false;
    } finally {
      setProcessingUsers(prev => ({ ...prev, [userId]: false }));
    }
  };

  // Handle activate user
  const handleActivateUser = async (userId) => {
    const result = await updateUserAccess(userId, 'activate');
    // if (result && onActivateUser) {
    //   onActivateUser(userId);
    // }
  };

  // Handle revoke user access
  const handleRevokeUser = async (userId) => {
    const result = await updateUserAccess(userId, 'revoke');
    // if (result && onDeactivateUser) {
    //   onDeactivateUser(userId);
    // }
  };

  // Handle admin notes change
  const handleNotesChange = (userId, notes) => {
    setAdminNotes(prev => ({ ...prev, [userId]: notes }));
    // if (onUpdateNotes) {
    //   onUpdateNotes(userId, notes);
    // }
  };

  // Extract team members and filter based on deal type restrictions
  const getMembers = () => {
    const members = [];

    if (Array.isArray(dealUsers)) {
      dealUsers.forEach(userItem => {
        const u = userItem?.user;
        if (u?.id) {
          members.push({
            id: u.id,
            firstName: u.first_name || '',
            lastName: u.last_name || '',
            fullName: `${u.first_name || ''} ${u.last_name || ''}`.trim(),
            email: extractEmail(u.email),
          });
        }
      });
    }

    if (Array.isArray(dealTeams)) {
      dealTeams.forEach(teamItem => {
        const users = teamItem?.team?.users;
        if (Array.isArray(users)) {
          users.forEach(user => {
            if (user?.id && !members.some(member => member.id === user.id)) {
              members.push({
                id: user.id,
                firstName: user.first_name || '',
                lastName: user.last_name || '',
                fullName: `${user.first_name || ''} ${user.last_name || ''}`.trim(),
                email: extractEmail(user.email),
              });
            }
          });
        }
      });
    }

    // NEW: Filter members based on deal type restrictions
    // Only show users who have the current deal_type in their restricted_deal_types
    if (!connectorId && dealRestrictions && dealType !== undefined && dealType !== null) {
      const filteredMembers = members.filter(member => {
        const userRestrictions = dealRestrictions[member.id];
        
        // If user has restrictions and the current deal_type is in their restricted list
        if (userRestrictions && Array.isArray(userRestrictions)) {
          const hasRestriction = userRestrictions.includes(dealType);
          return hasRestriction;
        }
        
        // If no restrictions, don't show this user
        return false;
      });
      
      return filteredMembers;
    }

    // If connector_id exists, show all members (original behavior)
    return members;
  };

  // Get authorization status for a user based on API response
  const getUserAuthorizationStatus = (userId) => {
    if (!authorizationData || !authorizationData.authorizations) {
      return { 
        isAuthorized: false, 
        authData: null, 
        buttonAction: 'activate' 
      };
    }

    // Find ALL authorization records for this user
    const userAuths = authorizationData.authorizations.filter(
      auth => auth.authorized_user && auth.authorized_user.id === parseInt(userId)
    );

    if (userAuths.length === 0) {
      return { 
        isAuthorized: false, 
        authData: null, 
        buttonAction: 'activate' 
      };
    }

    // CRITICAL FIX: Get the LATEST record (same logic as vouchers-service)
    // Sort by updated_at/created_at descending and take the first one
    const latestUserAuth = userAuths.sort((a, b) => {
      const aTime = new Date(a.modified || a.updated_at || a.created_at || a.created || '1970-01-01');
      const bTime = new Date(b.modified || b.updated_at || b.created_at || b.created || '1970-01-01');
      return bTime - aTime; // Descending order (latest first)
    })[0];

    const isActive = latestUserAuth.is_active === true;
    const buttonAction = isActive ? 'revoke' : 'activate';
    
    return {
      isAuthorized: isActive,
      authData: latestUserAuth,
      buttonAction: buttonAction
    };
  };

  const members = getMembers();

  // Show loading state while data is being loaded
  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '32px' }}>
        <Icon iconName="spinner" spin />
        <p>Loading voucher authorization data...</p>
      </div>
    );
  }

  if (members.length === 0) {
    return (
      <div>
        <div style={{display: 'flex', justifyContent: 'flex-end', marginBottom: '20px'}}>
          <Button 
            theme="secondary" 
            size="sm" 
            onClick={handleViewHistory}
            disabled={historyLoading}
          >
            <Icon iconName={historyLoading ? "spinner" : "history"} spin={historyLoading} />
            {historyLoading ? 'Loading...' : 'View History'}
          </Button>
        </div>
        <div style={{textAlign: 'center', padding: '32px', color: '#6b7280'}}>
          <Icon iconName="users" />
          <span>
            {!connectorId && dealType !== undefined && dealType !== null
              ? 'No team members with restrictions for this deal type.'
              : 'No team members found for this deal'}
          </span>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px'}}>
        <div style={{flex: 1}}>
          <h3 style={{margin: 0, color: '#374151', fontSize: '16px', fontWeight: '600'}}>
            {showHistory ? 'Voucher Access History' : 'Manage Voucher Access'}
          </h3>
          <p style={{margin: '4px 0 0 0', color: '#6b7280', fontSize: '14px', lineHeight: '1.4'}}>
            {showHistory 
              ? 'View complete history of all voucher access changes including admin actions, timestamps, and notes.'
              : 'Grant or revoke team members\' access to submit the vouchers and add notes to document authorization changes.'
            }
          </p>
        </div>
        <Button 
          theme="secondary" 
          size="sm" 
          onClick={handleViewHistory}
          disabled={historyLoading}
        >
          <Icon iconName={historyLoading ? 'spinner' : (showHistory ? 'times' : 'history')} spin={historyLoading} />
          {historyLoading ? 'Loading...' : (showHistory ? 'Hide History' : 'View History')}
        </Button>
      </div>
      
      {!showHistory ? (
        // Main table view
        <div className="connector-voucher-access__container">
          <div className="connector-voucher-access__table-header">
            <div className="connector-voucher-access__header-cell">MEMBER</div>
            <div className="connector-voucher-access__header-cell">STATUS</div>
            <div className="connector-voucher-access__header-cell">ADMIN NOTES</div>
            <div className="connector-voucher-access__header-cell">ACTION</div>
          </div>
          <div className="connector-voucher-access__table-body">
            {members.map((member) => {
            const authStatus = getUserAuthorizationStatus(member.id);
            const isAuthorized = authStatus.isAuthorized;
            const authData = authStatus.authData;
            const buttonAction = authStatus.buttonAction; // 'activate' or 'revoke' based on is_active status
            
            return (
              <div key={member.id} className="connector-voucher-access__table-row">
                <div className="connector-voucher-access__cell">
                  <span className="connector-voucher-access__member-name">
                    {member.fullName || 'Unknown User'}
                    {member.email && typeof member.email === 'string' && (
                      <div style={{fontSize: '11px', color: '#6b7280', marginTop: '2px', lineHeight: '1.2'}}>
                        {member.email}
                      </div>
                    )}
                  </span>
                </div>
                
                <div className="connector-voucher-access__cell">
                  <span className={`connector-voucher-access__status-badge connector-voucher-access__status-badge--${isAuthorized ? 'active' : 'inactive'}`}>
                    {isAuthorized ? 'Active' : 'Inactive'}
                  </span>
                </div>
                
                <div className="connector-voucher-access__cell">
                  <textarea
                    className="connector-voucher-access__admin-notes"
                    value={adminNotes[member.id] || authData?.admin_notes || ''}
                    placeholder="Admin able to type something"
                    rows="1"
                    onChange={(e) => handleNotesChange(member.id, e.target.value)}
                  />
                </div>
                
                <div className="connector-voucher-access__cell">
                  <Button
                    theme={buttonAction === 'revoke' ? 'warning' : 'primary'}
                    size="sm"
                    onClick={() => buttonAction === 'revoke' ? handleRevokeUser(member.id) : handleActivateUser(member.id)}
                    disabled={processingUsers[member.id]}
                  >
                    <Icon iconName={processingUsers[member.id] ? 'spinner' : (buttonAction === 'revoke' ? 'user-slash' : 'user-check')} spin={processingUsers[member.id]} />
                    {processingUsers[member.id] ? 'Processing...' : (buttonAction === 'revoke' ? 'Revoke' : 'Activate')}
                  </Button>
                </div>
              </div>
            );
          })}
          </div>
        </div>
      ) : (
        // History table view - replaces the main table
        <div className="admin-voucher-access-history">
          <div className="results-table">
            <div className="table-header">
              <div className="header-cell">ADMIN</div>
              <div className="header-cell">MEMBER</div>
              <div className="header-cell">STATUS</div>
              <div className="header-cell">CREATED</div>
              <div className="header-cell">SUBMITTED DATE</div>
              <div className="header-cell">ADMIN NOTES</div>
            </div>
            <div className="results-table-body">{historyLoading ? (
            <div className="table-row">
              <div className="cell" style={{gridColumn: '1 / -1', textAlign: 'center', padding: '32px'}}>
                <Icon iconName="spinner" spin />
                <span style={{marginLeft: '8px'}}>Loading history...</span>
              </div>
            </div>
          ) : historyData && historyData.history && historyData.history.length > 0 ? (
            historyData.history.map((auth, idx) => (
              <div className="table-row" key={auth.id || idx}>
                {/* ADMIN - First column */}
                <div className="cell">
                  <div className="member-info">
                    <div className="member-display-name">
                      {auth.admin_user.first_name} {auth.admin_user.last_name}
                    </div>
                    {extractEmail(auth.admin_user.email) && (
                      <div className="admin-user-email">
                        {extractEmail(auth.admin_user.email)}
                      </div>
                    )}
                  </div>
                </div>
                {/* MEMBER - Second column */}
                <div className="cell">
                  <div className="member-info">
                    <div className="member-display-name">
                      {auth.authorized_user.first_name} {auth.authorized_user.last_name}
                    </div>
                    {extractEmail(auth.authorized_user.email) && (
                      <div className="admin-user-email">
                        {extractEmail(auth.authorized_user.email)}
                      </div>
                    )}
                  </div>
                </div>
                {/* STATUS - Third column */}
                <div className="cell">
                  <span className={`status-badge ${auth.is_active ? 'active' : 'inactive'}`}>
                    {auth.action === 'activate' ? 'Activated' : 
                     auth.action === 'deactivate' ? 'Deactivated' : 'Revoked'}
                  </span>
                </div>
                {/* CREATED AT - Fourth column */}
                <div className="cell">
                  <span className="admin-created-date-text">
                    {new Date(auth.created).toLocaleDateString()} {new Date(auth.created).toLocaleTimeString()}
                  </span>
                </div>
                {/* SUBMISSION DATE - Fifth column */}
                <div className="cell">
                  {auth.is_voucher_submitted && auth.voucher_submitted_date ? (
                    <span className="admin-submission-date-text">
                      {new Date(auth.voucher_submitted_date).toLocaleDateString()} {new Date(auth.voucher_submitted_date).toLocaleTimeString()}
                    </span>
                  ) : (
                    <span>--</span>
                  )}
                </div>
                {/* ADMIN NOTES - Sixth column */}
                <div className="cell">
                  <span title={auth.admin_notes || '--'}>{auth.admin_notes || '--'}</span>
                </div>
              </div>
            ))
          ) : (
            <div className="table-row">
              <div className="cell" style={{gridColumn: '1 / -1', textAlign: 'center', padding: '32px'}}>
                No authorization history available
              </div>
            </div>
          )}
          </div>
        </div>
        </div>
      )}
    </div>
  );
};

AdminConnectorVoucherAccessNew.propTypes = {
  dealId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  connectorId: PropTypes.string,
  dealType: PropTypes.string, // Deal type for restriction filtering
  adminUserId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  dealUsers: PropTypes.array,
  dealTeams: PropTypes.array,
  dealRestrictions: PropTypes.object, // Passed from parent to avoid duplicate fetch
};

AdminConnectorVoucherAccessNew.defaultProps = {
  dealUsers: [],
  dealTeams: [],
  connectorId: null,
  dealType: null,
  adminUserId: null,
  dealRestrictions: null,
};

export default AdminConnectorVoucherAccessNew;
